/**
 * Dates for India: DD/MM/YYYY, 12-hour time, business dates in the
 * restaurant's time zone. See docs/architecture/ordering-billing-gst.md.
 */

export const DEFAULT_TIME_ZONE = "Asia/Kolkata";
export const DEFAULT_DAY_START_HOUR = 4;

/**
 * Zones whose offset from UTC never changes, in minutes. India has kept UTC+05:30
 * without daylight saving since 1945, so its wall clock is plain arithmetic.
 * The first Intl.DateTimeFormat with a time zone loads the zone's data, which
 * cost a mid-range phone over 100 ms on the storefront's first screen, where
 * offers are checked against the clock (P9-T21). Other zones go through Intl.
 */
const FIXED_OFFSET_MINUTES: Readonly<Record<string, number>> = {
	"Asia/Kolkata": 330,
	"Asia/Calcutta": 330,
};

/** The wall clock of `instant` in a fixed-offset zone, read with the UTC getters; else null. */
function fixedWallClock(instant: Date, timeZone: string): Date | null {
	const offset = FIXED_OFFSET_MINUTES[timeZone];
	const time = instant.getTime();
	if (offset === undefined || !Number.isFinite(time)) return null;
	return new Date(time + offset * 60_000);
}

const pad2 = (n: number) => String(n).padStart(2, "0");

const ymdFormatters = new Map<string, Intl.DateTimeFormat>();

function ymdParts(instant: Date, timeZone: string): { year: string; month: string; day: string } {
	const wall = fixedWallClock(instant, timeZone);
	if (wall)
		return {
			year: String(wall.getUTCFullYear()),
			month: pad2(wall.getUTCMonth() + 1),
			day: pad2(wall.getUTCDate()),
		};
	let fmt = ymdFormatters.get(timeZone);
	if (!fmt) {
		fmt = new Intl.DateTimeFormat("en-CA", {
			timeZone,
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
		});
		ymdFormatters.set(timeZone, fmt);
	}
	const parts = Object.fromEntries(fmt.formatToParts(instant).map((p) => [p.type, p.value]));
	return { year: parts.year ?? "", month: parts.month ?? "", day: parts.day ?? "" };
}

/**
 * The business date (YYYY-MM-DD) of an instant: the calendar date in the
 * restaurant's time zone after subtracting the day start hour. With the default
 * 04:00 start, a bill at 01:30 belongs to the previous business date.
 */
export function businessDate(
	instant: Date,
	timeZone: string = DEFAULT_TIME_ZONE,
	dayStartHour: number = DEFAULT_DAY_START_HOUR,
): string {
	const shifted = new Date(instant.getTime() - dayStartHour * 60 * 60 * 1000);
	const { year, month, day } = ymdParts(shifted, timeZone);
	return `${year}-${month}-${day}`;
}

/**
 * A restaurant's business date for an instant: its time zone and the day
 * start hour. The hour is 04:00 for every restaurant; there is no setting for
 * it. Order and KOT numbers restart with each business date.
 */
export function restaurantBusinessDate(restaurant: { timezone: string }, instant: Date): string {
	return businessDate(instant, restaurant.timezone || DEFAULT_TIME_ZONE, DEFAULT_DAY_START_HOUR);
}

const BUSINESS_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * The Indian financial year (1 April to 31 March) of a business date, as four
 * digits: "2026-09-28" → "2627", "2027-03-31" → "2627", "2027-04-01" → "2728".
 */
export function financialYear(date: string): string {
	const match = BUSINESS_DATE.exec(date);
	if (!match) throw new RangeError(`Not a business date: ${date}`);
	const year = Number(match[1]);
	const month = Number(match[2]);
	const start = month >= 4 ? year : year - 1;
	return `${String(start % 100).padStart(2, "0")}${String((start + 1) % 100).padStart(2, "0")}`;
}

/** DD/MM/YYYY in the given time zone. */
export function formatDate(instant: Date, timeZone: string = DEFAULT_TIME_ZONE): string {
	const { year, month, day } = ymdParts(instant, timeZone);
	return `${day}/${month}/${year}`;
}

/** DD/MM/YYYY for a business date string, without time zone conversion. */
export function formatBusinessDate(date: string): string {
	const match = BUSINESS_DATE.exec(date);
	if (!match) throw new RangeError(`Not a business date: ${date}`);
	return `${match[3]}/${match[2]}/${match[1]}`;
}

const timeFormatters = new Map<string, Intl.DateTimeFormat>();

/** 12-hour time such as "1:30 pm", in the given time zone. */
export function formatTime(instant: Date, timeZone: string = DEFAULT_TIME_ZONE): string {
	let fmt = timeFormatters.get(timeZone);
	if (!fmt) {
		fmt = new Intl.DateTimeFormat("en-IN", {
			timeZone,
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		});
		timeFormatters.set(timeZone, fmt);
	}
	return fmt.format(instant).replace(/ |\s/g, " ").toLowerCase();
}

/** "28/09/2026, 1:30 pm" */
export function formatDateTime(instant: Date, timeZone: string = DEFAULT_TIME_ZONE): string {
	return `${formatDate(instant, timeZone)}, ${formatTime(instant, timeZone)}`;
}

/** A time of day "HH:MM" (24 hour, as stored in opening hours) as "6:30 pm". */
export function formatClock(time: string): string {
	const match = /^(\d{2}):(\d{2})$/.exec(time);
	if (!match) return time;
	const hours = Number(match[1]);
	const suffix = hours < 12 ? "am" : "pm";
	return `${hours % 12 === 0 ? 12 : hours % 12}:${match[2]} ${suffix}`;
}

const localFormatters = new Map<string, Intl.DateTimeFormat>();

/**
 * The wall clock of an instant in a time zone: the calendar date, the ISO
 * weekday (1 Monday to 7 Sunday) and "HH:MM". Offers are scheduled in it.
 */
export function localParts(
	instant: Date,
	timeZone: string = DEFAULT_TIME_ZONE,
): { date: string; isoDay: number; time: string } {
	const wall = fixedWallClock(instant, timeZone);
	if (wall) {
		const weekday = wall.getUTCDay();
		return {
			date: `${wall.getUTCFullYear()}-${pad2(wall.getUTCMonth() + 1)}-${pad2(wall.getUTCDate())}`,
			isoDay: weekday === 0 ? 7 : weekday,
			time: `${pad2(wall.getUTCHours())}:${pad2(wall.getUTCMinutes())}`,
		};
	}
	let fmt = localFormatters.get(timeZone);
	if (!fmt) {
		fmt = new Intl.DateTimeFormat("en-GB", {
			timeZone,
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
			hourCycle: "h23",
		});
		localFormatters.set(timeZone, fmt);
	}
	const p = Object.fromEntries(fmt.formatToParts(instant).map((x) => [x.type, x.value]));
	const date = `${p.year}-${p.month}-${p.day}`;
	const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
	return { date, isoDay: weekday === 0 ? 7 : weekday, time: `${p.hour}:${p.minute}` };
}

/**
 * The instant at which the wall clock in a time zone reads `date` `time`
 * ("YYYY-MM-DD", "HH:MM"): an offer's first and last day as the owner picks
 * them. Exact for zones without daylight saving, such as India's.
 */
export function instantOfLocalTime(
	date: string,
	time: string,
	timeZone: string = DEFAULT_TIME_ZONE,
): Date {
	const asUtc = new Date(`${date}T${time}:00Z`);
	// The zone's offset at that moment: how far its wall clock is from UTC.
	const shown = localParts(asUtc, timeZone);
	const shownUtc = new Date(`${shown.date}T${shown.time}:00Z`).getTime();
	return new Date(asUtc.getTime() - (shownUtc - asUtc.getTime()));
}

/** The calendar date ("YYYY-MM-DD") `days` days after `date` (before, when negative). */
export function addDays(date: string, days: number): string {
	const d = new Date(`${date}T00:00:00Z`);
	d.setUTCDate(d.getUTCDate() + days);
	return d.toISOString().slice(0, 10);
}
